'use strict'

const values_ = require('lodash/values')
const includes_ = require('lodash/includes')
const merge_ = require('lodash/merge')
const negate_ = require('lodash/negate')
const pick_ = require('lodash/pick')

const createDatasetController = require('../dataset-controller/controller')
const DATASET_TYPES = require('dbsm-common/src/datasetTypes')
const sendBiOnError = require('dbsm-common/src/bi/sendBiOnError')
const addRavenData = require('dbsm-common/src/raven/addRavenData')
const schemaAPICreator = require('./schemaAPI')
const wixDataProxyCreator = require('../wix-data/wixDataProxy')
const { isDatasetError } = require('../dataset-controller/DatasetError')
const errorBoundariesCreator = require('../error-boundaries/errorBoundaries')
const createRavenProxy = require('../raven/ravenProxy')
const createFedopsLogger = require('fedops-logger/dist/src/logger-factory')
  .create
const createRavenHooksForFedops = require('dbsm-common/src/raven/createRavenHooksForFedops')
const { parseUrlPattern } = require('dbsm-common/src/dynamic-pages/urlUtils')

const extractDynamicUrlPatternFieldsValuesFromRecord = (dynamicUrl, record) => {
  const patternFields =
    dynamicUrl && record ? parseUrlPattern(dynamicUrl).fields : null
  return patternFields ? pick_(record, patternFields) : null
}

const extractRouterPayload = (payload, parser) => {
  const { dynamicUrl, userDefinedFilter, items, totalCount } = payload
  const parsedItems = parser(items)
  const record = parsedItems ? parsedItems[0] : null
  const dynamicUrlPatternFieldsValues = extractDynamicUrlPatternFieldsValuesFromRecord(
    dynamicUrl,
    record
  )

  return {
    prefetchedData: {
      items: parsedItems,
      totalCount
    },
    dynamicPagesData: {
      dynamicUrl,
      userDefinedFilter,
      dynamicUrlPatternFieldsValues
    }
  }
}

const mergeRouterDatasetConfig = (routerConfig, controllerConfig) =>
  merge_({}, controllerConfig, routerConfig)

module.exports = ({
  wixDataCreator,
  wixSdk,
  elementorySupport,
  errorReporter,
  Raven,
  UserRaven,
  WixDataRaven,
  biLogger,
  errorBiLogger
}) => {
  const { startHook, endHook } = createRavenHooksForFedops(Raven)
  // custom timeoutHook for viewer-app fedops logger. Logs with level info until stabilized, to prevent false alarms
  const timeoutHook = ({ name, timeout }) =>
    Raven.captureMessage(`interaction ${name} timed out`, {
      level: 'info',
      extra: {
        interactionTimeout: timeout
      }
    })
  const fedopsLogger = createFedopsLogger('wix-code-dbsm-viewer-app', {
    interactionTimeout: 50000,
    startHook,
    endHook,
    timeoutHook
  })

  const ravenProxy = createRavenProxy(Raven)
  const userRavenProxy = createRavenProxy(UserRaven)
  const wixDataRavenProxy = createRavenProxy(WixDataRaven)
  const errorBoundaries = errorBoundariesCreator({
    Raven: ravenProxy,
    UserRaven: userRavenProxy,
    WixDataRaven: wixDataRavenProxy
  })

  let wixDataProxy
  let wixDataSchemasProxy
  let routerPayload
  let platformUtilities
  let schemaAPI

  sendBiOnError(errorBiLogger, Raven, negate_(isDatasetError))

  return {
    initAppForPage: ({ routerReturnedData }, platformUtils) => {
      const { timeoutId } = fedopsLogger.interactionStarted(
        'databinding/initAppForPage'
      )
      addRavenData(Raven, routerReturnedData, 'routerReturnedData')
      try {
        ;({ wixDataProxy, wixDataSchemasProxy } = wixDataProxyCreator(
          errorBoundaries.wixDataCodeZone,
          wixDataCreator
        ))
        routerPayload = routerReturnedData || {}
        platformUtilities = platformUtils
        schemaAPI = schemaAPICreator(wixDataSchemasProxy, Raven)
        fedopsLogger.interactionEnded('databinding/initAppForPage', {
          timeoutId
        })
        return Promise.resolve()
      } catch (e) {
        Raven.captureException(e)
        return Promise.reject(e)
      }
    },

    createControllers: controllerConfigs => {
      try {
        const {
          timeoutId: createControllersTimeoutId
        } = fedopsLogger.interactionStarted('databinding/createControllers')
        if (controllerConfigs.length) {
          const {
            timeoutId: loadSchemasTimeoutId
          } = fedopsLogger.interactionStarted('databinding/loadSchemas')
          schemaAPI
            .loadSchemas()
            .then(() => {
              fedopsLogger.interactionEnded('databinding/loadSchemas', {
                timeoutId: loadSchemasTimeoutId
              })
            })
            .catch(() => {})
        }

        const datasetTypes = values_(DATASET_TYPES)
        const controllers = controllerConfigs.map(
          ({ type, config, connections, $w }, controllerIndex) => {
            if (!includes_(datasetTypes, type)) {
              throw new Error(
                `type of controller MUST be one of ${datasetTypes} but is ${type}`
              )
            }
            const uniqueId = `${controllerIndex}_${type}`
            const datasetConfiguration =
              type === DATASET_TYPES.ROUTER_DATASET
                ? mergeRouterDatasetConfig(routerPayload.config, config)
                : config

            const routerData =
              type === DATASET_TYPES.ROUTER_DATASET
                ? extractRouterPayload(
                    routerPayload,
                    elementorySupport.wireFormatParser
                  )
                : undefined

            const datasetController = createDatasetController({
              controllerConfig: datasetConfiguration,
              datasetType: type,
              connections,
              wixDataProxy,
              wixDataSchemasProxy,
              firePlatformEvent: errorBoundaries.userCodeZone($w.fireEvent),
              errorBoundaries,
              wixSdk,
              errorReporter,
              platformUtilities,
              routerData,
              ravenProxy,
              uniqueId,
              schemaAPI,
              fedopsLogger,
              biLogger
            })
            return Promise.resolve(datasetController)
          }
        )
        fedopsLogger.interactionEnded('databinding/createControllers', {
          timeoutId: createControllersTimeoutId
        })
        return controllers
      } catch (e) {
        Raven.captureException(e)
        return []
      }
    }
  }
}



// WEBPACK FOOTER //
// ./viewer-app-module/appCreator.js